import React, { useCallback } from 'react';

import { useForm } from 'react-hook-form';

import { Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { Autocomplete, Input, Select } from '~/components/Common/Forms/fields';

import { SUPPLIER_NAMES } from '~/consts/bedbank';
import { booleanOptions, countriesIsoOptions, ratingOperations, ratingValues } from '~/consts/bedbankFilterOptions';

import { toTitleCase } from '~/utils/toTitleCase';

import { FilterQuery } from './type';

type CountryOption = {
  label: string;
  value: string;
};

interface Props {
  queries: FilterQuery;
  handleSubmit: (query?: FilterQuery) => void;
}

function SearchForm(props: Props) {
  const { control, reset, handleSubmit } = useForm({
    defaultValues: {
      filterByName: props.queries.filterByName,
      filterBySupplier: props.queries.filterBySupplier,
      filterById: props.queries.filterById,
      filterByRatingOp: props.queries.filterByRatingOp,
      filterByRatingVal: props.queries.filterByRatingVal,
      filterByMapped: props.queries.filterByMapped,
      filterByHasImages: props.queries.filterByHasImages,
      filterByHasRooms: props.queries.filterByHasRooms,
      filterByCountryCode: (props.queries.filterByCountryCode ?? [])
        .map((x) => countriesIsoOptions.find((n) => n.value === x))
        .filter(Boolean),
    },
    mode: 'onBlur',
  });

  const clearFilters = useCallback(() => {
    reset();
    props.handleSubmit();
    window.location.reload();
  }, [reset, props.handleSubmit]);

  return (
    <Stack direction="column" spacing={2}>
      <Box>
        <Typography variant="h6">Filter By</Typography>
      </Box>
      <Box>
        <form
          onSubmit={handleSubmit((q) =>
            props.handleSubmit({
              filterByName: q.filterByName,
              filterBySupplier: q.filterBySupplier,
              filterById: q.filterById,
              filterByRatingOp: q.filterByRatingOp,
              filterByRatingVal: q.filterByRatingVal,
              filterByMapped: q.filterByMapped,
              filterByHasImages: q.filterByHasImages,
              filterByHasRooms: q.filterByHasRooms,
              filterByCountryCode: q.filterByCountryCode?.map((x: CountryOption) => x.value),
            }),
          )}
        >
          <Grid spacing={2} container mb={2}>
            <Grid size={{ md: 4 }}>
              <Input fullWidth control={control} muiLabel="Enter Hotel Name" name="filterByName" />
            </Grid>
            <Grid size={{ md: 4 }}>
              <Input fullWidth control={control} muiLabel="Enter Supplier Property Id" name="filterById" />
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterBySupplier" muiLabel="Supplier">
                {['all', ...SUPPLIER_NAMES].map((supplier) => (
                  <MenuItem key={supplier} value={supplier === 'all' ? '' : supplier}>
                    {toTitleCase(supplier)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 3 }}>
              <Stack direction="row" spacing={1}>
                <Select control={control} name="filterByRatingOp" muiLabel="Rating operator">
                  {ratingOperations.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select control={control} name="filterByRatingVal" muiLabel="Rating value">
                  {ratingValues.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Grid>
            <Grid size={{ md: 4 }}>
              <Autocomplete
                multiple
                control={control}
                getOptionLabel={(option: CountryOption) => option.label}
                name="filterByCountryCode"
                muiLabel="Countries"
                options={countriesIsoOptions}
              />
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByMapped" muiLabel="Has mapped?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByHasRooms" muiLabel="Has rooms?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ md: 2 }}>
              <Select control={control} name="filterByHasImages" muiLabel="Has images?">
                {[{ label: ' - ', value: '' }, ...booleanOptions].map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Box>
              <Button type="button" variant="contained" onClick={clearFilters}>
                Clear
              </Button>
            </Box>
            <Box>
              <Button type="submit" variant="contained">
                Filter
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </Stack>
  );
}

export default SearchForm;
